// Globals
import { InfoCircleOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Form, FormItem } from '@/components';
import { Input, Select } from '@/ui';

// Helpers
import { CreateChannelFormFields, getChannelsOptions, TCreateChannelFormValues } from '../../helpers';
import { useChannelFormRules } from '../../hooks';

// Models
import { ChannelType, IChannel } from '@/models/channel.model';
import { ChannelTypeFilterValues, ModelSearchTypeFilterValues } from '../../helpers/types';

type TChannelFormProps = {
  onSubmit: (values: TCreateChannelFormValues) => void;
  children?: React.ReactNode;
  initialValues?: Partial<TCreateChannelFormValues>;
  channels?: IChannel[] | null;
  isCreating?: boolean;
};

const ChannelForm = ({ onSubmit, children, initialValues, channels, isCreating }: TChannelFormProps) => {
  const rules = useChannelFormRules();
  const { t } = useTranslation(['common']);
  const options = useMemo(() => getChannelsOptions(channels), [channels]);
  const [channelType, setChannelType] = useState<string>(
    initialValues?.[CreateChannelFormFields.TYPE] ?? ChannelType.PRODUCTS,
  );

  return (
    <Form onFinish={onSubmit} initialValues={initialValues}>
      <FormItem
        name={CreateChannelFormFields.NAME}
        label={t('channel_name')}
        rules={rules[CreateChannelFormFields.NAME]}
      >
        <Input placeholder={t('channel_name_placeholder')} />
      </FormItem>
      {/* <FormItem name={CreateChannelFormFields.DESCRIPTION} label={t('label_llm_description')}>
        <TextArea placeholder={t('label_llm_description')} autoSize={{ minRows: 4, maxRows: 10 }} />
      </FormItem> */}
      <FormItem
        name={CreateChannelFormFields.SLUG}
        label={t('channel_code')}
        rules={rules[CreateChannelFormFields.SLUG]}
        tooltip={{
          title: t('channel_code_help'),
          icon: <InfoCircleOutlined />,
        }}
      >
        <Input disabled={!isCreating} placeholder={t('channel_slug_placeholder')} />
      </FormItem>
      <FormItem
        name={CreateChannelFormFields.TYPE}
        label={t('channel_type')}
        rules={rules[CreateChannelFormFields.TYPE]}
      >
        <Select
          disabled={!isCreating}
          options={ChannelTypeFilterValues.filter((type) => type.value !== '')}
          onChange={(value) => setChannelType(value)}
        />
      </FormItem>
      {channelType === ChannelType.PARTS && (
        <FormItem
          name={CreateChannelFormFields.MODEL_SEARCH_TYPE}
          label={t('model_search_type')}
          rules={rules[CreateChannelFormFields.MODEL_SEARCH_TYPE]}
        >
          <Select options={ModelSearchTypeFilterValues} />
        </FormItem>
      )}
      <FormItem
        name={CreateChannelFormFields.SITE_URL}
        label={t('channel_url')}
        rules={rules[CreateChannelFormFields.SITE_URL]}
      >
        <Input placeholder={t('channel_url_placeholder')} />
      </FormItem>
      {channels && (
        <FormItem
          name={CreateChannelFormFields.COPY_CHANNEL_ID}
          label={t('channel_copy')}
          rules={rules[CreateChannelFormFields.COPY_CHANNEL_ID]}
        >
          <Select options={options} placeholder={t('channel_copy_placeholder')} />
        </FormItem>
      )}
      {children}
    </Form>
  );
};

export default ChannelForm;
